<div class="bp-landing">

  <header class="bp-nav">
    <a class="bp-nav__brand" routerLink="/">
      <img src="assets/img/boothpilot/boothpilot-lockup.svg" alt="BoothPilot">
    </a>
    <div class="bp-nav__end">
      <app-legal-lang-toggle [lang]="lang" (langChange)="onLangChange($event)"></app-legal-lang-toggle>
      <a class="bp-btn bp-btn--dark" routerLink="/user/login">{{ lang === 'fr' ? 'Se connecter' : 'Sign in' }}</a>
    </div>
  </header>

  @if (lang === 'en') {
    <section class="bp-page-head">
      <h1 class="bp-page-head__title">Applications</h1>
      <p class="bp-page-head__lead">
        BoothPilot is video software for sports teams. It comes as two applications that share one account:
        BoothPilot on the web, which records and broadcasts games from a fixed camera with nobody operating
        it, and BoothPilot Replay on mobile, which works either with a fixed camera or with someone filming
        by hand. This page describes what each one does, and exactly what Google data each one requests and
        why.
      </p>
    </section>

    <section class="bp-products">
      <div class="bp-products__grid">
        <div class="bp-product">
          <p class="bp-product__platform">Web <span class="bp-product__status">Closed beta</span></p>
          <h2 class="bp-product__title">BoothPilot</h2>
          <p class="bp-product__body">
            Scheduling, automated capture and live broadcast. You add your games to the team calendar; on
            game day a fixed camera in the booth records and streams with nobody operating it, and the
            finished footage lands in the tool your coaches already use.
          </p>
          <ul class="bp-product__list">
            <li>Season and game scheduling</li>
            <li>Automated recording from a fixed camera</li>
            <li>Live broadcast to your team's own YouTube channel</li>
            <li>Automatic upload to coaching tools</li>
          </ul>
          <p class="bp-product__services">
            Uses YouTube to create and run the live broadcasts you schedule, on a channel your team owns.
            <a routerLink="/privacy" fragment="boothpilot">How we use your data</a>
          </p>
        </div>
        <div class="bp-product">
          <div class="bp-product__meta">
            <p class="bp-product__platform">Mobile <span class="bp-product__status">Public launch soon</span></p>
            <a class="bp-product__pricing-link" routerLink="/pricing">View planned pricing</a>
          </div>
          <h2 class="bp-product__title">BoothPilot Replay</h2>
          <p class="bp-product__body">
            A cloud-based replay app for the sideline. No automated setup is needed: whoever is free — a
            parent, an assistant coach — films by hand on a phone or tablet, and Replay works the same way
            with a fixed camera if you have one. Record short clips from one or more angles during the game
            and the bench reviews them seconds later, annotated. Clips are stored in a cloud account your
            team owns rather than on our servers — Google Drive is the supported provider today.
          </p>
          <ul class="bp-product__list">
            <li>Filmed by hand, by a fixed camera, or both at once</li>
            <li>Multi-angle clip capture from the sideline</li>
            <li>Near-instant replay</li>
            <li>Draw and annotate on top of a clip</li>
            <li>Clips stay in your team's own cloud storage</li>
          </ul>
          <p class="bp-product__services">
            Uses Google Drive to store and play back your team's clips.
            <a routerLink="/privacy" fragment="replay">How we use your data</a>
          </p>
        </div>
      </div>
    </section>

    <section class="bp-pricing" id="replay-pricing">
      <div class="bp-pricing__head">
        <p class="bp-pricing__eyebrow">Planned Replay pricing</p>
        <h2 class="bp-section-title">Every feature is free for Fall 2026</h2>
        <p class="bp-pricing__lead">Use the complete BoothPilot Replay experience at no cost throughout Fall 2026. These plans are an early look at our intended pricing afterward—not active subscriptions. All prices and limits remain provisional until launch.</p>
        <p class="bp-pricing__notice">Nothing is locked today. No plan selection or payment details are required. Intended prices are in USD, per team.</p>
      </div>
      <div class="bp-pricing__grid">
        <article class="bp-plan"><p class="bp-plan__name">Starter</p><p class="bp-plan__audience">For a coach recording solo</p><p class="bp-plan__price">Free</p><ul><li>1 camera angle</li><li>Built-in camera recording</li><li>1 viewer</li><li>ODK tagging</li></ul></article>
        <article class="bp-plan bp-plan--featured"><span class="bp-plan__badge">Best for most teams</span><p class="bp-plan__name">Basic</p><p class="bp-plan__audience">For a small sideline crew</p><p class="bp-plan__price">$10 <small>USD / team / month</small></p><ul><li>1 camera angle</li><li>Built-in camera recording</li><li>Up to 3 viewers</li><li>Full tagging</li></ul></article>
        <article class="bp-plan"><p class="bp-plan__name">Multi-Angle</p><p class="bp-plan__audience">For coordinated game-day review</p><p class="bp-plan__price">$40 <small>USD / team / month</small></p><ul><li>Up to 2 camera angles</li><li>HDMI capture for 1 angle</li><li>Unlimited viewers</li><li>Full tagging</li></ul></article>
        <article class="bp-plan"><p class="bp-plan__name">Full</p><p class="bp-plan__audience">For larger programs and crews</p><p class="bp-plan__price">$80 <small>USD / team / month</small></p><ul><li>Unlimited camera angles</li><li>Built-in camera + HDMI capture</li><li>Unlimited viewers</li><li>Full tagging</li></ul></article>
      </div>
      <p class="bp-pricing__fineprint"><strong>Pricing preview:</strong> all plan names, feature limits, and prices are provisional and subject to change until the paid plans officially launch. An angle means one simultaneous camera feed. HDMI capture requires compatible hardware.</p>
    </section>

    <section class="bp-permissions">
      <h2 class="bp-section-title">Data and permissions</h2>
      <p class="bp-permissions__intro">
        BoothPilot asks for the Google access its features need, and only after you choose to connect an
        account. Nothing below is required to browse this site. Here is exactly what we request and why.
      </p>

      <div class="bp-permissions__item">
        <h3 class="bp-permissions__app">BoothPilot (web) — YouTube</h3>
        <p class="bp-permissions__scope">https://www.googleapis.com/auth/youtube.force-ssl</p>
        <p class="bp-permissions__body">
          <strong>Why we request it:</strong> to create and run the live broadcasts you schedule in
          BoothPilot — creating the broadcast and its stream, binding them to the channel you connected, and
          reading back their status so the app can show whether you are live.
        </p>
        <p class="bp-permissions__body">
          <strong>What we do not do:</strong> we do not read, change or delete anything else on your channel,
          we do not post or comment as you, and we never use this data for advertising.
        </p>
        <p class="bp-permissions__body">
          <strong>Revoking:</strong> use the Revoke button on the Integrations screen in BoothPilot, or
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener">Google account
            permissions</a>. Revoking deletes the stored tokens and broadcast data from BoothPilot.
        </p>
      </div>

      <div class="bp-permissions__item">
        <h3 class="bp-permissions__app">BoothPilot Replay (mobile) — Google Drive</h3>
        <p class="bp-permissions__scope">https://www.googleapis.com/auth/drive.file</p>
        <p class="bp-permissions__body">
          <strong>Why we request it:</strong> to upload your team's clips and thumbnails to the Drive account
          your team links, read them back for playback, and delete them when you delete a clip. This scope
          grants access only to files the app itself created — it cannot reach anything else in your Drive.
        </p>
        <p class="bp-permissions__body">
          <strong>What we do not do:</strong> your video files stay in your team's own Drive; we store only
          the Drive file identifiers, never the media. Never used for advertising.
        </p>
        <p class="bp-permissions__body">
          <strong>Revoking:</strong> a team administrator can disconnect the Drive account from Team
          settings, or from
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener">Google account
            permissions</a>.
        </p>
      </div>

      <div class="bp-permissions__item">
        <h3 class="bp-permissions__app">Signing in with Google (optional)</h3>
        <p class="bp-permissions__body">
          If you choose to sign in with a Google account we receive your name, email address and profile
          picture, and request no other Google service through it. Signing in with an email address and
          password instead is always available.
        </p>
      </div>

      <p class="bp-permissions__note">
        Our use and transfer of information received from Google APIs follows the
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank"
          rel="noopener">Google API Services User Data Policy</a>, including its Limited Use requirements.
        Full detail is in our <a routerLink="/privacy">Privacy Policy</a>.
      </p>
    </section>
  } @else {
    <section class="bp-page-head">
      <h1 class="bp-page-head__title">Applications</h1>
      <p class="bp-page-head__lead">
        BoothPilot est un logiciel vidéo pour les équipes sportives. Il se décline en deux applications qui
        partagent un même compte : BoothPilot sur le Web, qui filme et diffuse les matchs depuis une caméra
        fixe sans que personne ne l'opère, et BoothPilot Replay sur mobile, qui fonctionne aussi bien avec
        une caméra fixe qu'avec une personne qui filme à la main. Cette page décrit ce que fait chacune
        d'elles, ainsi que les données Google que chacune demande et pourquoi.
      </p>
    </section>

    <section class="bp-products">
      <div class="bp-products__grid">
        <div class="bp-product">
          <p class="bp-product__platform">Web <span class="bp-product__status">Bêta fermée</span></p>
          <h2 class="bp-product__title">BoothPilot</h2>
          <p class="bp-product__body">
            Planification, captation automatique et diffusion en direct. Vous ajoutez vos matchs au
            calendrier de l'équipe; le jour du match, une caméra fixe installée dans la passerelle filme et
            diffuse sans opérateur, et les séquences terminées se retrouvent dans l'outil que vos entraîneurs
            utilisent déjà.
          </p>
          <ul class="bp-product__list">
            <li>Planification de la saison et des matchs</li>
            <li>Enregistrement automatique depuis une caméra fixe</li>
            <li>Diffusion en direct sur la chaîne YouTube de votre équipe</li>
            <li>Téléversement automatique vers vos outils d'entraînement</li>
          </ul>
          <p class="bp-product__services">
            Utilise YouTube pour créer et gérer les diffusions en direct que vous planifiez, sur une chaîne
            qui appartient à votre équipe.
            <a routerLink="/privacy" [queryParams]="{ lang: 'fr' }" fragment="boothpilot-fr">Comment nous
              utilisons vos données</a>
          </p>
        </div>
        <div class="bp-product">
          <div class="bp-product__meta">
            <p class="bp-product__platform">Mobile <span class="bp-product__status">Lancement public à venir</span></p>
            <a class="bp-product__pricing-link" routerLink="/pricing" [queryParams]="{ lang: 'fr' }">Voir la tarification</a>
          </div>
          <h2 class="bp-product__title">BoothPilot Replay</h2>
          <p class="bp-product__body">
            Une application de reprise vidéo en nuage pour le bord du terrain. Aucune installation
            automatisée n'est nécessaire : la personne disponible — un parent, un entraîneur adjoint — filme
            à la main avec un téléphone ou une tablette, et Replay fonctionne de la même façon avec une
            caméra fixe si vous en avez une. Captez de courtes séquences sous un ou plusieurs angles pendant
            le match et l'équipe les revoit quelques secondes plus tard, annotées. Les séquences sont
            stockées dans un compte en nuage qui appartient à votre équipe plutôt que sur nos serveurs —
            Google Drive est le fournisseur pris en charge aujourd'hui.
          </p>
          <ul class="bp-product__list">
            <li>Filmé à la main, par une caméra fixe, ou les deux à la fois</li>
            <li>Captation de séquences multi-angles depuis le bord du terrain</li>
            <li>Reprise quasi instantanée</li>
            <li>Dessin et annotations par-dessus une séquence</li>
            <li>Les séquences demeurent dans le stockage en nuage de votre équipe</li>
          </ul>
          <p class="bp-product__services">
            Utilise Google Drive pour stocker et relire les séquences de votre équipe.
            <a routerLink="/privacy" [queryParams]="{ lang: 'fr' }" fragment="replay-fr">Comment nous
              utilisons vos données</a>
          </p>
        </div>
      </div>
    </section>

    <section class="bp-pricing" id="replay-pricing">
      <div class="bp-pricing__head">
        <p class="bp-pricing__eyebrow">Tarification prévue de Replay</p>
        <h2 class="bp-section-title">Toutes les fonctionnalités sont gratuites à l’automne 2026</h2>
        <p class="bp-pricing__lead">Profitez de l’expérience BoothPilot Replay complète sans frais pendant toute la saison d’automne 2026. Ces forfaits présentent notre tarification prévue par la suite — ce ne sont pas des abonnements actifs. Tous les prix et toutes les limites demeurent provisoires jusqu’au lancement.</p>
        <p class="bp-pricing__notice">Rien n’est verrouillé aujourd’hui. Aucun choix de forfait ni renseignement de paiement n’est requis. Les prix prévus sont en dollars américains, par équipe.</p>
      </div>
      <div class="bp-pricing__grid">
        <article class="bp-plan"><p class="bp-plan__name">Départ</p><p class="bp-plan__audience">Pour un entraîneur qui filme seul</p><p class="bp-plan__price">Gratuit</p><ul><li>1 angle de caméra</li><li>Enregistrement avec la caméra intégrée</li><li>1 spectateur</li><li>Étiquetage ODK</li></ul></article>
        <article class="bp-plan bp-plan--featured"><span class="bp-plan__badge">Idéal pour la plupart des équipes</span><p class="bp-plan__name">De base</p><p class="bp-plan__audience">Pour une petite équipe sur la ligne de côté</p><p class="bp-plan__price">10 $ <small>US / équipe / mois</small></p><ul><li>1 angle de caméra</li><li>Enregistrement avec la caméra intégrée</li><li>Jusqu’à 3 spectateurs</li><li>Étiquetage complet</li></ul></article>
        <article class="bp-plan"><p class="bp-plan__name">Multi-angle</p><p class="bp-plan__audience">Pour une révision coordonnée les jours de match</p><p class="bp-plan__price">40 $ <small>US / équipe / mois</small></p><ul><li>Jusqu’à 2 angles de caméra</li><li>Captation HDMI pour 1 angle</li><li>Spectateurs illimités</li><li>Étiquetage complet</li></ul></article>
        <article class="bp-plan"><p class="bp-plan__name">Complet</p><p class="bp-plan__audience">Pour les grands programmes et équipes</p><p class="bp-plan__price">80 $ <small>US / équipe / mois</small></p><ul><li>Angles de caméra illimités</li><li>Caméra intégrée + captation HDMI</li><li>Spectateurs illimités</li><li>Étiquetage complet</li></ul></article>
      </div>
      <p class="bp-pricing__fineprint"><strong>Aperçu de la tarification :</strong> tous les noms de forfaits, les limites de fonctionnalités et les prix sont provisoires et peuvent changer jusqu’au lancement officiel des forfaits payants. Un angle correspond à un flux de caméra simultané. La captation HDMI nécessite du matériel compatible.</p>
    </section>

    <section class="bp-permissions">
      <h2 class="bp-section-title">Données et autorisations</h2>
      <p class="bp-permissions__intro">
        BoothPilot demande les accès Google dont ses fonctions ont besoin, et seulement après que vous ayez
        choisi de connecter un compte. Rien de ce qui suit n'est nécessaire pour consulter ce site. Voici
        exactement ce que nous demandons et pourquoi.
      </p>

      <div class="bp-permissions__item">
        <h3 class="bp-permissions__app">BoothPilot (Web) — YouTube</h3>
        <p class="bp-permissions__scope">https://www.googleapis.com/auth/youtube.force-ssl</p>
        <p class="bp-permissions__body">
          <strong>Pourquoi nous la demandons :</strong> pour créer et gérer les diffusions en direct que vous
          planifiez dans BoothPilot — créer la diffusion et son flux, les associer à la chaîne que vous avez
          connectée et en consulter l'état pour que l'application puisse indiquer si vous êtes en direct.
        </p>
        <p class="bp-permissions__body">
          <strong>Ce que nous ne faisons pas :</strong> nous ne consultons, ne modifions ni ne supprimons rien
          d'autre sur votre chaîne, nous ne publions ni ne commentons en votre nom, et ces données ne servent
          jamais à des fins publicitaires.
        </p>
        <p class="bp-permissions__body">
          <strong>Révocation :</strong> utilisez le bouton de révocation à l'écran des intégrations dans
          BoothPilot, ou la page des
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener">autorisations de
            votre compte Google</a>. La révocation supprime de BoothPilot les jetons conservés et les données
          de diffusion.
        </p>
      </div>

      <div class="bp-permissions__item">
        <h3 class="bp-permissions__app">BoothPilot Replay (mobile) — Google Drive</h3>
        <p class="bp-permissions__scope">https://www.googleapis.com/auth/drive.file</p>
        <p class="bp-permissions__body">
          <strong>Pourquoi nous la demandons :</strong> pour téléverser les séquences et les vignettes de
          votre équipe vers le compte Drive que votre équipe a lié, les relire aux fins de lecture et les
          supprimer lorsque vous supprimez une séquence. Cette portée ne donne accès qu'aux fichiers créés par
          l'application elle-même — elle ne peut atteindre aucun autre fichier de votre Drive.
        </p>
        <p class="bp-permissions__body">
          <strong>Ce que nous ne faisons pas :</strong> vos fichiers vidéo demeurent dans le Drive de votre
          équipe; nous ne conservons que les identifiants de fichiers Drive, jamais les fichiers
          multimédias. Jamais utilisés à des fins publicitaires.
        </p>
        <p class="bp-permissions__body">
          <strong>Révocation :</strong> un administrateur d'équipe peut dissocier le compte Drive depuis les
          paramètres d'équipe, ou à partir des
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener">autorisations de
            votre compte Google</a>.
        </p>
      </div>

      <div class="bp-permissions__item">
        <h3 class="bp-permissions__app">Connexion avec Google (facultative)</h3>
        <p class="bp-permissions__body">
          Si vous choisissez de vous connecter avec un compte Google, nous recevons votre nom, votre adresse
          courriel et votre photo de profil, et nous ne demandons aucun autre service Google par ce moyen. La
          connexion avec une adresse courriel et un mot de passe demeure toujours offerte.
        </p>
      </div>

      <p class="bp-permissions__note">
        Notre utilisation et notre transfert des renseignements reçus des API Google respectent la
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank"
          rel="noopener">politique relative aux données utilisateur des services d'API Google</a>, y compris
        ses exigences d'utilisation limitée. Tous les détails se trouvent dans notre
        <a routerLink="/privacy" [queryParams]="{ lang: 'fr' }">politique de confidentialité</a>.
      </p>
    </section>
  }

  <footer class="bp-footer">
    <span>© 2026 BoothPilot — Laurent Chicoine Productions</span>
    <span class="bp-footer__links">
      @if (lang === 'en') {
        <a routerLink="/">Home</a>
        <a routerLink="/privacy">Privacy Policy</a>
        <a routerLink="/tos">Terms of Service</a>
        <a href="mailto:hostmaster&#64;boothpilot.com">Contact</a>
      } @else {
        <a routerLink="/" [queryParams]="{ lang: 'fr' }">Accueil</a>
        <a routerLink="/privacy" [queryParams]="{ lang: 'fr' }">Politique de confidentialité</a>
        <a routerLink="/tos" [queryParams]="{ lang: 'fr' }">Conditions d'utilisation</a>
        <a href="mailto:hostmaster&#64;boothpilot.com">Nous joindre</a>
      }
    </span>
  </footer>

</div>
